import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Layout, FilterBar, VideoGrid, LoadingState, ErrorState, VideoModal, ChannelModal, InfoSection, StatsPage, ChannelPage, ChannelsHubPage, RankingShareCard, ComparisonPage } from './components';
import { useTopVideos } from './hooks/useTopVideos';
import { useRecords } from './hooks/useRecords';
import { useBest } from './hooks/useBest';
import { useStats } from './hooks/useStats';
import { useChannel } from './hooks/useChannel';
import { useTopChannels } from './hooks/useTopChannels';
import { useCategories } from './hooks/useCategories';
import { useRising } from './hooks/useRising';
import { useUrlStateSync, parseUrlState, copyCurrentUrl } from './hooks/useUrlState';
import { type PageView, WINDOW_LABEL } from './utils/routes';
import {
  type Lang, SUPPORTED_LANGS, LANGS, isLang, getUi, localizedSeo,
  regionName as i18nRegionName, categoryName as i18nCategoryName,
} from '../functions/lib/i18n';
import { trackFilterChange, trackRandomVideo, trackShareLink } from './utils/analytics';
import { REGIONS, Video, DEFAULT_PERIOD } from './types';
import './index.css';

/**
 * Format large numbers with abbreviations
 */
function formatTotalViews(views: number): string {
  if (views >= 1_000_000_000_000) return (views / 1_000_000_000_000).toFixed(1) + 'T';
  if (views >= 1_000_000_000) return (views / 1_000_000_000).toFixed(1) + 'B';
  if (views >= 1_000_000) return (views / 1_000_000).toFixed(1) + 'M';
  return views.toLocaleString();
}

/**
 * Friendly "last updated" label for the freshness indicator.
 */
function formatUpdated(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Parse ISO 8601 duration to seconds
 */
function parseDurationToSeconds(duration: string): number {
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 0;
  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseInt(match[3] || '0', 10);
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Main App component - LTS Simplified Version
 */
function App() {
  // Initialize filters from URL or defaults
  const initialState = useMemo(() => parseUrlState(), []);

  const [category, setCategoryState] = useState(initialState.category);
  const [region, setRegionState] = useState(initialState.region);
  const [view, setViewState] = useState<PageView>(initialState.view);
  const [timeWindow, setTimeWindowState] = useState<string | undefined>(initialState.window);
  const [pageChannelId, setPageChannelId] = useState<string | undefined>(initialState.channelId);
  const [compareIds, setCompareIds] = useState<[string, string] | undefined>(initialState.compareIds);
  const [compareVideoSelection, setCompareVideoSelection] = useState<Video[]>([]);
  const [compareChannelSelection, setCompareChannelSelection] = useState<{ channelId: string; title: string }[]>([]);
  const [lang, setLangState] = useState<Lang>(initialState.lang);
  const [langSuggestion, setLangSuggestion] = useState<Lang | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showShorts, setShowShortsState] = useState(initialState.shorts);

  // LTS: Fixed period to trending only
  const period = DEFAULT_PERIOD;

  // Wrapper setters with analytics tracking
  const setCategory = useCallback((value: string) => {
    trackFilterChange('category', value);
    setCategoryState(value);
  }, []);
  const setRegion = useCallback((value: string) => {
    trackFilterChange('region', value);
    setRegionState(value);
  }, []);
  const setShowShorts = useCallback((value: boolean) => {
    trackFilterChange('shorts', value ? 'on' : 'off');
    setShowShortsState(value);
  }, []);
  const setView = useCallback((value: PageView) => {
    trackFilterChange('view', value);
    setViewState(value);
    if (value !== 'channel' && value !== 'channels') setPageChannelId(undefined);
    if (value !== 'chart') setTimeWindowState(undefined); // windows only apply to the chart view
  }, []);
  const openChannelPage = useCallback((id: string) => {
    trackFilterChange('view', 'channel');
    setViewState('channel');
    setPageChannelId(id);
    setActiveChannelId(null);
  }, []);
  const setTimeWindow = useCallback((value: string | undefined) => {
    trackFilterChange('window', value || 'trending');
    setTimeWindowState(value);
    if (value) setViewState('chart'); // windows apply to the chart view
  }, []);
  const setLang = useCallback((value: Lang) => {
    trackFilterChange('lang', value);
    setLangState(value);
    setLangSuggestion(null);
  }, []);
  const toggleCompareVideo = useCallback((video: Video) => {
    setCompareVideoSelection((current) => current.some((v) => v.id === video.id)
      ? current.filter((v) => v.id !== video.id)
      : [...current.slice(-1), video]);
  }, []);
  const toggleCompareChannel = useCallback((channel: { channelId: string; title: string }) => {
    setCompareChannelSelection((current) => current.some((c) => c.channelId === channel.channelId)
      ? current.filter((c) => c.channelId !== channel.channelId)
      : [...current.slice(-1), channel]);
  }, []);
  const openComparison = useCallback((type: 'videos' | 'channels', ids: [string, string]) => {
    setCompareIds(ids);
    setViewState(type === 'videos' ? 'compare-videos' : 'compare-channels');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Sync filters to the URL path
  const urlState = useMemo(() => ({
    region,
    category,
    shorts: showShorts,
    view,
    lang,
    window: timeWindow,
    channelId: pageChannelId,
    compareIds,
  }), [region, category, showShorts, view, lang, timeWindow, pageChannelId, compareIds]);

  useUrlStateSync(urlState, initialState, (newState) => {
    // Handle browser back/forward - use state setters directly to avoid double tracking
    setRegionState(newState.region);
    setCategoryState(newState.category);
    setShowShortsState(newState.shorts);
    setViewState(newState.view);
    setLangState(newState.lang);
    setTimeWindowState(newState.window);
    setPageChannelId(newState.channelId);
    setCompareIds(newState.compareIds);
  });
  const [activeVideo, setActiveVideo] = useState<Video | null>(null);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [miniPlayer, setMiniPlayer] = useState<Video | null>(null);
  const [watchLater, setWatchLater] = useState<Video[]>([]);
  const [showWatchLater, setShowWatchLater] = useState(false);
  const [newVideosAvailable, setNewVideosAvailable] = useState(false);
  const [isFilterStuck, setIsFilterStuck] = useState(false);
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  });
  const [watchedToday, setWatchedToday] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('watched_today');
      if (stored) {
        try {
          const { date, ids } = JSON.parse(stored);
          if (date === new Date().toDateString()) return ids;
        } catch { /* ignore */ }
      }
    }
    return [];
  });
  const [linkCopied, setLinkCopied] = useState(false);

  const filterBarRef = useRef<HTMLDivElement>(null);
  const lastActivityRef = useRef(Date.now());
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval>>();

  const { categories } = useCategories();
  const isRecords = view === 'records';
  const isStats = view === 'stats';
  const isChannel = view === 'channel';
  const isChannelsHub = view === 'channels';
  const isRising = view === 'rising';
  const isComparison = view === 'compare-videos' || view === 'compare-channels';
  const isWindow = view === 'chart' && !!timeWindow;
  const chart = useTopVideos(category, period, region, !isRecords && !isStats && !isChannel && !isChannelsHub && !isWindow && !isRising && !isComparison);
  const records = useRecords(category, isRecords);
  const best = useBest(region, category, timeWindow || 'w1', isWindow);
  const rising = useRising(region, category, isRising);
  const { stats, isLoading: statsLoading, error: statsError, refetch: statsRefetch } = useStats(isStats);
  const {
    channel: activeChannel, title: activeChannelTitle, totals: activeChannelTotals,
    videos: activeChannelVideos, isLoading: channelLoading, error: channelError,
  } = useChannel(activeChannelId);
  const {
    channel: pageChannel, title: pageChannelTitle, totals: pageChannelTotals,
    videos: pageChannelVideos, isLoading: pageChannelLoading, error: pageChannelError,
    refetch: refetchPageChannel,
  } = useChannel(isChannel ? pageChannelId ?? null : null);
  const {
    channels: topChannels, isLoading: topChannelsLoading, error: topChannelsError, refetch: refetchTopChannels,
  } = useTopChannels(isChannelsHub);
  const { videos: rawVideos, isLoading, error, refetch, fetchedAt } =
    isRecords ? records : isWindow ? best : isRising ? rising : chart;
  const currentRegion = REGIONS.find((r) => r.id === region);

  // Filter out short videos if showShorts is false
  const videos = useMemo(() => {
    if (showShorts) return rawVideos;
    return rawVideos.filter((video: Video) => parseDurationToSeconds(video.duration) >= 120);
  }, [rawVideos, showShorts]);

  const currentCategory = categories.find((c) => c.id === category);
  const totalViews = videos.reduce((sum, v) => sum + v.viewCount, 0);

  // Localized UI labels + SEO copy. English keeps its hand-written wording
  // (seoStrings stays null); other languages render from the shared i18n pack,
  // matching what the server rendered so crawlers see consistent content.
  const catId = category === '0' ? 'ALL' : category;
  const ui = getUi(lang);
  const seoStrings = lang === 'en' ? null : localizedSeo(
    lang,
    isRecords ? 'records' : 'chart',
    {
      hasCat: catId !== 'ALL',
      isWorld: region === 'ALL',
      catName: i18nCategoryName(lang, catId, currentCategory?.name || ''),
      regionName: i18nRegionName(lang, region, currentRegion?.name || ''),
    }
  );

  // Gamification - calculate level
  const watchedCount = watchedToday.length;
  const explorerLevel = watchedCount >= 50 ? 5 : watchedCount >= 25 ? 4 : watchedCount >= 10 ? 3 : watchedCount >= 5 ? 2 : watchedCount >= 1 ? 1 : 0;
  const levelNames = ['', 'Viewer', 'Explorer', 'Enthusiast', 'Trendsetter', 'Legend'];
  const levelEmojis = ['', '👀', '🔍', '🔥', '⭐', '👑'];

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Auto-detect the visitor's language and *suggest* (never force) switching.
  // Only on the English root, only if they haven't dismissed it before.
  useEffect(() => {
    if (lang !== 'en') return;
    try {
      if (localStorage.getItem('lang_suggest_dismissed') === '1') return;
      const nav = (navigator.language || '').slice(0, 2).toLowerCase();
      if (isLang(nav) && nav !== 'en') setLangSuggestion(nav as Lang);
    } catch { /* ignore */ }
  }, [lang]);

  const dismissLangSuggestion = useCallback(() => {
    try { localStorage.setItem('lang_suggest_dismissed', '1'); } catch { /* ignore */ }
    setLangSuggestion(null);
  }, []);

  // Keep the document title & meta description in sync with the current view (SEO)
  useEffect(() => {
    document.documentElement.setAttribute('lang', LANGS[lang].htmlLang);
    document.documentElement.setAttribute('dir', LANGS[lang].dir || 'ltr');

    // Non-English: use the shared localized strings (matches the server render).
    if (seoStrings) {
      document.title = seoStrings.title;
      document.querySelector('meta[name="description"]')?.setAttribute('content', seoStrings.description);
      return;
    }

    if (isStats) {
      document.title = 'YouTube Trending Stats — YouTubeMost';
      document.querySelector('meta[name="description"]')?.setAttribute(
        'content',
        'Live numbers behind YouTubeMost: videos and views tracked, the categories and countries gaining the most views this week, this week’s biggest mover, and the all-time view record.'
      );
      return;
    }

    if (isRising) {
      const place = region === 'ALL' ? 'Worldwide' : `in ${currentRegion?.name || region}`;
      document.title = `Fastest Growing YouTube Videos ${place} — YouTubeMost`;
      document.querySelector('meta[name="description"]')?.setAttribute('content', `Videos gaining YouTube views fastest ${place.toLowerCase()}, ranked from stored historical snapshots.`);
      return;
    }

    if (isChannelsHub) {
      document.title = 'Top YouTube Channels by Views — YouTubeMost';
      document.querySelector('meta[name="description"]')?.setAttribute(
        'content',
        'The YouTube channels with the most tracked views, ranked by total views across every video we have recorded.'
      );
      return;
    }

    if (isChannel) {
      const name = pageChannel?.title || pageChannelTitle;
      if (name) {
        document.title = `${name} — Most Viewed YouTube Videos | YouTubeMost`;
        document.querySelector('meta[name="description"]')?.setAttribute(
          'content',
          `The most-viewed YouTube videos from ${name}, ranked by view count.`
        );
      }
      return;
    }

    const categoryName = category !== 'ALL' && currentCategory ? ` ${currentCategory.name}` : '';

    if (isWindow && timeWindow) {
      const label = WINDOW_LABEL[timeWindow];
      const regionName = region !== 'ALL' && currentRegion ? ` in ${currentRegion.name}` : ' Worldwide';
      document.title = `Top${categoryName} YouTube Videos of the ${label}${regionName} — YouTubeMost`;
      const description =
        `The${categoryName ? categoryName.toLowerCase() : ''} videos that gained the most views on YouTube over the ${label.toLowerCase()}${regionName.toLowerCase()}, ranked by views gained.`;
      document.querySelector('meta[name="description"]')?.setAttribute('content', description);
      return;
    }

    if (isRecords) {
      document.title = `Most Viewed${categoryName} YouTube Videos of All Time — YouTubeMost`;
      const description =
        `The most viewed${categoryName ? categoryName.toLowerCase() : ''} YouTube videos of all time, ranked by total view count. ` +
        `See which videos have the most views ever, updated as records are broken.`;
      document.querySelector('meta[name="description"]')?.setAttribute('content', description);
      return;
    }

    const regionName = region !== 'ALL' && currentRegion ? ` in ${currentRegion.name}` : ' Worldwide';
    document.title = `Trending${categoryName} YouTube Videos${regionName} — YouTubeMost`;

    const description =
      `The most popular${categoryName ? categoryName.toLowerCase() : ''} YouTube videos${regionName.toLowerCase()} right now, ` +
      `ranked by views and refreshed continuously. Browse the top trending videos and watch them in one place.`;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
  }, [region, category, currentRegion, currentCategory, isRecords, isStats, isChannel, isChannelsHub, isRising, pageChannel, pageChannelTitle, isWindow, timeWindow, lang, seoStrings]);

  // Note: the ItemList JSON-LD is now rendered server-side in functions/_middleware.ts
  // so crawlers see it without executing JS (and to avoid duplicate structured data).

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  // Sticky filter bar detection
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      if (filterBarRef.current) {
        const rect = filterBarRef.current.getBoundingClientRect();
        setIsFilterStuck(rect.top <= 80);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-refresh check every 10 minutes (live trending only)
  useEffect(() => {
    if (isRecords || isWindow || isStats || isChannel || isChannelsHub) return;
    refreshIntervalRef.current = setInterval(async () => {
      try {
        const categoryId = category === '0' ? 'ALL' : category;
        const params = new URLSearchParams({
          region,
          category: categoryId,
          time: period,
          shorts: 'true',
          limit: '100'
        });
        const response = await fetch(`/api/videos?${params.toString()}`);
        const data = await response.json() as { generatedAt?: number };
        const newFetchedAt = data.generatedAt ? new Date(data.generatedAt).toISOString() : null;
        if (newFetchedAt && newFetchedAt !== fetchedAt) {
          setNewVideosAvailable(true);
        }
      } catch {
        // Silently fail
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [category, period, region, fetchedAt, isRecords, isWindow, isStats, isChannel, isChannelsHub]);

  // Idle detection (30 seconds)
  useEffect(() => {
    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);

      idleTimeoutRef.current = setTimeout(() => {
        if (!activeVideo && !miniPlayer) {
          setShowIdleModal(true);
        }
      }, 60 * 1000); // 60 seconds idle
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetIdleTimer, { passive: true }));
    resetIdleTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetIdleTimer));
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    };
  }, [activeVideo, miniPlayer]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;

      const key = e.key.toLowerCase();

      // Number keys 1-9 for categories
      if (/^[1-9]$/.test(key) && categories.length > 0) {
        const idx = parseInt(key) - 1;
        if (idx < categories.length) {
          setCategory(categories[idx].id);
          e.preventDefault();
        }
      }

      // S for shorts toggle
      if (key === 's' && !e.metaKey && !e.ctrlKey) {
        setShowShorts(!showShorts);
        e.preventDefault();
      }

      // R for random video
      if (key === 'r' && !e.metaKey && !e.ctrlKey) {
        openRandomVideo();
        e.preventDefault();
      }

      // Arrow keys for video navigation
      if (key === 'arrowdown' && !activeVideo) {
        setSelectedIndex(prev => Math.min(prev + 1, videos.length - 1));
        e.preventDefault();
      }
      if (key === 'arrowup' && !activeVideo) {
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        e.preventDefault();
      }

      // Enter to play selected
      if (key === 'enter' && selectedIndex >= 0 && selectedIndex < videos.length) {
        setActiveVideo(videos[selectedIndex]);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [categories, videos, selectedIndex, activeVideo, showShorts, setShowShorts]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openRandomVideo = useCallback(() => {
    if (videos.length > 0) {
      const randomVideo = videos[Math.floor(Math.random() * videos.length)];
      trackRandomVideo();
      setActiveVideo(randomVideo);
    }
  }, [videos]);

  // Track watched videos
  const trackWatched = useCallback((videoId: string) => {
    setWatchedToday(prev => {
      if (prev.includes(videoId)) return prev;
      const updated = [...prev, videoId];
      localStorage.setItem('watched_today', JSON.stringify({ date: new Date().toDateString(), ids: updated }));
      return updated;
    });
  }, []);

  // Copy link to clipboard
  const handleCopyLink = useCallback(async () => {
    const success = await copyCurrentUrl();
    if (success) {
      trackShareLink();
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, []);

  // Handle modal close - convert to mini player
  const handleModalClose = useCallback(() => {
    if (activeVideo) {
      setMiniPlayer(activeVideo);
      trackWatched(activeVideo.id);
    }
    setActiveVideo(null);
  }, [activeVideo, trackWatched]);

  // Watch later functions
  const addToWatchLater = useCallback((video: Video) => {
    setWatchLater(prev => {
      if (prev.find(v => v.id === video.id)) return prev;
      return [...prev, video];
    });
  }, []);

  const removeFromWatchLater = useCallback((videoId: string) => {
    setWatchLater(prev => prev.filter(v => v.id !== videoId));
  }, []);

  const handleRefresh = useCallback(() => {
    setNewVideosAvailable(false);
    refetch();
    scrollToTop();
  }, [refetch]);

  return (
    <Layout
      theme={theme}
      onToggleTheme={toggleTheme}
      lang={lang}
      languages={SUPPORTED_LANGS.map((l) => ({ code: l, native: LANGS[l].native }))}
      onLangChange={(code) => setLang(code as Lang)}
    >
      <div className="container">
        {/* New Videos Available Banner */}
        {newVideosAvailable && (
          <div className="new-videos-banner" onClick={handleRefresh}>
            ✨ New trending videos available — click to refresh
          </div>
        )}

        {!isStats && !isChannel && !isChannelsHub && !isComparison && (
          <FilterBar
            ref={filterBarRef}
            selectedCategory={category}
            selectedRegion={region}
            categories={categories}
            showShorts={showShorts}
            onCategoryChange={setCategory}
            onRegionChange={setRegion}
            onShowShortsChange={setShowShorts}
            onRandom={openRandomVideo}
            onShare={handleCopyLink}
            shareCopied={linkCopied}
            disabled={isLoading}
            isStuck={isFilterStuck}
            lang={lang}
          />
        )}

        {langSuggestion && (
          <div className="lang-suggest-banner">
            <span>{getUi(langSuggestion).suggest(LANGS[langSuggestion].native)}</span>
            <div className="lang-suggest-actions">
              <button className="lang-suggest-yes" onClick={() => setLang(langSuggestion)}>
                {LANGS[langSuggestion].native}
              </button>
              <button className="lang-suggest-no" onClick={dismissLangSuggestion}>
                {getUi(langSuggestion).dismiss}
              </button>
            </div>
          </div>
        )}

        <div className="toolbar-row">
          <div className="view-toggle" role="tablist" aria-label="View">
            <button
              role="tab"
              aria-selected={!isRecords && !isStats && !isChannel && !isChannelsHub && !isWindow && !isRising}
              className={`view-toggle-btn${!isRecords && !isStats && !isChannel && !isChannelsHub && !isWindow && !isRising ? ' active' : ''}`}
              onClick={() => { setView('chart'); setTimeWindow(undefined); }}
            >
              🔥 {ui.trendingTab}
            </button>
            {lang === 'en' && (
              <button role="tab" aria-selected={isRising} className={`view-toggle-btn${isRising ? ' active' : ''}`} onClick={() => setView('rising')}>
                🚀 Rising Fast
              </button>
            )}
            {lang === 'en' && (
              <button
                role="tab"
                aria-selected={isWindow}
                className={`view-toggle-btn${isWindow ? ' active' : ''}`}
                onClick={() => setTimeWindow('w1')}
              >
                📈 Best Performers
              </button>
            )}
            {lang === 'en' && (
              <button
                role="tab"
                aria-selected={isStats}
                className={`view-toggle-btn${isStats ? ' active' : ''}`}
                onClick={() => setView('stats')}
              >
                📊 Stats
              </button>
            )}
            {lang === 'en' && (
              <button
                role="tab"
                aria-selected={isChannelsHub}
                className={`view-toggle-btn${isChannelsHub ? ' active' : ''}`}
                onClick={() => setView('channels')}
              >
                🏆 Channels
              </button>
            )}
          </div>
        </div>

        {isComparison && compareIds ? (
          <ComparisonPage type={view === 'compare-videos' ? 'videos' : 'channels'} ids={compareIds} />
        ) : isStats ? (
          <div className="results-header">
            <h1 className="results-title">📊 YouTube Trending Stats</h1>
          </div>
        ) : isChannelsHub ? (
          <div className="results-header">
            <h1 className="results-title">🏆 Top YouTube Channels by Views</h1>
          </div>
        ) : isChannel ? (
          <div className="results-header">
            <h1 className="results-title">
              📺 {pageChannel?.title || pageChannelTitle || 'Channel'}
            </h1>
          </div>
        ) : (
        <>
        <div className="results-header">
          <h1 className="results-title">
            {isRising ? (
              <>🚀 Fastest Growing{category !== 'ALL' && ` ${currentCategory?.name}`} YouTube Videos{region !== 'ALL' ? ` in ${currentRegion?.flag} ${currentRegion?.name}` : ' Worldwide'}</>
            ) : isWindow && timeWindow ? (
              <>
                📈 Top{category !== 'ALL' && ` ${currentCategory?.name}`} Videos of the {WINDOW_LABEL[timeWindow]}
                {region !== 'ALL' && ` in ${currentRegion?.flag} ${currentRegion?.name}`}
              </>
            ) : seoStrings ? (
              <>{isRecords ? '🏆 ' : '🔥 '}{seoStrings.h1}</>
            ) : isRecords ? (
              <>
                🏆 Most Viewed{category !== 'ALL' && ` ${currentCategory?.name}`} Videos of All Time
              </>
            ) : (
              <>
                🔥 Trending YouTube Videos
                {region !== 'ALL' && ` in ${currentRegion?.flag} ${currentRegion?.name}`}
                {category !== 'ALL' && ` • ${currentCategory?.name}`}
              </>
            )}
          </h1>
          {!isLoading && !error && videos.length > 0 && (
            <div className="results-stats">
              <span className="results-stat"><strong>{videos.length}</strong> videos</span>
              <span className="results-stat"><strong>{formatTotalViews(totalViews)}</strong> total views</span>
              {fetchedAt && (
                <span className="results-stat updated">🕒 Updated {formatUpdated(fetchedAt)}</span>
              )}
              {watchedCount > 0 && (
                <span className="results-stat gamification">
                  {levelEmojis[explorerLevel]} {levelNames[explorerLevel]}
                  {explorerLevel > 0 && ` Lv.${explorerLevel}`} · {watchedCount} watched
                </span>
              )}
            </div>
          )}
        </div>
        {isRising ? (
          <p className="results-intro">Videos gaining views fastest in the latest stored daily window, ranked by views per hour. This page uses historical snapshots and makes no live YouTube API request.</p>
        ) : isWindow && timeWindow ? (
          <p className="results-intro">
            The videos with the biggest jump in views on YouTube over the {WINDOW_LABEL[timeWindow].toLowerCase()}
            {region !== 'ALL' && currentRegion ? ` in ${currentRegion.name}` : ' worldwide'}
            {category !== 'ALL' && currentCategory ? ` in the ${currentCategory.name} category` : ''}
            , ranked by views gained rather than all-time total. Tap any video to watch it right here.
          </p>
        ) : seoStrings ? (
          <p className="results-intro">{seoStrings.intro}</p>
        ) : isRecords ? (
          <p className="results-intro">
            The most viewed{category !== 'ALL' && currentCategory ? ` ${currentCategory.name.toLowerCase()}` : ''} videos
            on YouTube of all time, ranked by total view count. This ranking is built from our own historical
            tracking and updates automatically as new records are set. Tap any video to watch it right here.
          </p>
        ) : (
          <p className="results-intro">
            The most popular videos on YouTube right now
            {region !== 'ALL' && currentRegion ? ` in ${currentRegion.name}` : ' worldwide'}
            {category !== 'ALL' && currentCategory ? ` in the ${currentCategory.name} category` : ''}
            , ranked by view count and refreshed continuously. Tap any video to watch it right here.
          </p>
        )}
        {((isRecords) || (isWindow)) && !isLoading && !error && videos.length === 0 && (
          <p className="results-intro" style={{ opacity: 0.7 }}>
            {isWindow
              ? 'This timeframe is still filling in from our snapshots — try a longer window or check back soon.'
              : 'All-time records are still being compiled from our daily snapshots — check back soon.'}
          </p>
        )}

        {isLoading && <LoadingState />}
        {error && <ErrorState message={error} onRetry={refetch} />}

        {!isLoading && !error && (
          <>
          {videos[0] && <RankingShareCard video={videos[0]} context={isRising ? 'Rising Fast' : region === 'ALL' ? 'Trending Worldwide' : `Trending in ${currentRegion?.name}`} />}
          {videos.length > 1 && <p className="ranking-compare-link"><a href={`/compare/videos/${videos[0].id}/${videos[1].id}`}>Compare the top two videos →</a></p>}
          <VideoGrid
            videos={videos}
            onPlayVideo={setActiveVideo}
            onAddToWatchLater={addToWatchLater}
            watchLaterIds={watchLater.map(v => v.id)}
            selectedIndex={selectedIndex}
            onChannelClick={setActiveChannelId}
            compareIds={compareVideoSelection.map((v) => v.id)}
            onCompareToggle={toggleCompareVideo}
          />
          </>
        )}
        </>
        )}

        {isStats && (
          <>
            <p className="results-intro">
              A look under the hood: how much data YouTubeMost tracks, and which categories, countries and
              videos are gaining the most views right now.
            </p>
            {statsLoading && <LoadingState />}
            {statsError && <ErrorState message={statsError} onRetry={statsRefetch} />}
            {stats && !statsLoading && !statsError && <StatsPage stats={stats} />}
          </>
        )}

        {isChannelsHub && (
          <>
            <p className="results-intro">
              Every channel here is ranked by the combined views of its videos we have tracked. Tap a channel
              to see its most-viewed videos.
            </p>
            {topChannelsLoading && <LoadingState />}
            {topChannelsError && <ErrorState message={topChannelsError} onRetry={refetchTopChannels} />}
            {!topChannelsLoading && !topChannelsError && (
              <ChannelsHubPage channels={topChannels} onSelectChannel={openChannelPage} compareIds={compareChannelSelection.map((c) => c.channelId)} onCompareToggle={toggleCompareChannel} />
            )}
          </>
        )}

        {!isComparison && compareVideoSelection.length > 0 && !isChannelsHub && (
          <aside className="compare-tray" aria-live="polite">
            <div><strong>Compare videos</strong><span>{compareVideoSelection.length === 1 ? 'Select one more video' : `${compareVideoSelection[0].title} vs ${compareVideoSelection[1].title}`}</span></div>
            <button disabled={compareVideoSelection.length !== 2} onClick={() => openComparison('videos', [compareVideoSelection[0].id, compareVideoSelection[1].id])}>Compare now</button>
            <button className="compare-tray-clear" onClick={() => setCompareVideoSelection([])} aria-label="Clear comparison">×</button>
          </aside>
        )}
        {!isComparison && isChannelsHub && compareChannelSelection.length > 0 && (
          <aside className="compare-tray" aria-live="polite">
            <div><strong>Compare channels</strong><span>{compareChannelSelection.length === 1 ? 'Select one more channel' : `${compareChannelSelection[0].title} vs ${compareChannelSelection[1].title}`}</span></div>
            <button disabled={compareChannelSelection.length !== 2} onClick={() => openComparison('channels', [compareChannelSelection[0].channelId, compareChannelSelection[1].channelId])}>Compare now</button>
            <button className="compare-tray-clear" onClick={() => setCompareChannelSelection([])} aria-label="Clear comparison">×</button>
          </aside>
        )}

        {isChannel && (
          <>
            {pageChannelLoading && <LoadingState />}
            {pageChannelError && <ErrorState message={pageChannelError} onRetry={refetchPageChannel} />}
            {!pageChannelLoading && !pageChannelError && pageChannelId && (
              <ChannelPage
                channelId={pageChannelId}
                channel={pageChannel}
                title={pageChannelTitle}
                totals={pageChannelTotals}
                videos={pageChannelVideos}
                onPlayVideo={setActiveVideo}
              />
            )}
          </>
        )}

        {!isComparison && (
          <nav className="seo-links" aria-label="Explore YouTube rankings">
            <h2>Explore YouTube rankings</h2>
            <div><a href="/">Trending worldwide</a><a href="/rising/youtube/worldwide">Rising fast</a><a href="/trending/youtube/united-states">United States</a><a href="/trending/youtube/india">India</a><a href="/trending/youtube/united-kingdom">United Kingdom</a><a href="/trending/youtube/worldwide/music">Trending music</a><a href="/trending/youtube/worldwide/gaming">Trending gaming</a><a href="/trending/youtube/worldwide/last-week">Top videos this week</a></div>
          </nav>
        )}
        <InfoSection lang={lang} />

        {/* Video Modal */}
        <VideoModal
          video={activeVideo}
          onClose={handleModalClose}
          allVideos={videos}
          onPlayVideo={setActiveVideo}
        />

        {/* Channel Modal */}
        <ChannelModal
          channelId={activeChannelId}
          channel={activeChannel}
          title={activeChannelTitle}
          totals={activeChannelTotals}
          trackedVideos={activeChannelVideos}
          isLoading={channelLoading}
          error={channelError}
          allVideos={videos}
          onClose={() => setActiveChannelId(null)}
          onPlayVideo={(v) => { setActiveChannelId(null); setActiveVideo(v); }}
          onOpenChannelPage={openChannelPage}
          lang={lang}
        />

        {/* Mini Player */}
        {miniPlayer && !activeVideo && (
          <div className="mini-player">
            <div className="mini-player-content">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${miniPlayer.id}?rel=0&modestbranding=1`}
                title={miniPlayer.title}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mini-player-controls">
              <span className="mini-player-title">{miniPlayer.title}</span>
              <button
                className="mini-player-expand"
                onClick={() => { setActiveVideo(miniPlayer); setMiniPlayer(null); }}
                title="Expand"
              >
                ↗
              </button>
              <button
                className="mini-player-close"
                onClick={() => setMiniPlayer(null)}
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Watch Later Toggle & Panel */}
        {watchLater.length > 0 && (
          <>
            {!showWatchLater ? (
              <button className="watch-later-toggle" onClick={() => setShowWatchLater(true)}>
                📋 Watch Later <span className="count">{watchLater.length}</span>
              </button>
            ) : (
              <div className="watch-later-panel">
                <div className="watch-later-header">
                  <h4>📋 Watch Later ({watchLater.length})</h4>
                  <button className="watch-later-clear" onClick={() => { setWatchLater([]); setShowWatchLater(false); }}>
                    Clear all
                  </button>
                </div>
                <div className="watch-later-list">
                  {watchLater.map(video => (
                    <div
                      key={video.id}
                      className="watch-later-item"
                      onClick={() => { setActiveVideo(video); setShowWatchLater(false); }}
                    >
                      <img src={video.thumbnail} alt="" />
                      <div className="watch-later-item-info">
                        <div className="watch-later-item-title">{video.title}</div>
                        <div className="watch-later-item-channel">{video.channelTitle}</div>
                      </div>
                      <button
                        className="watch-later-item-remove"
                        onClick={(e) => { e.stopPropagation(); removeFromWatchLater(video.id); }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  className="watch-later-toggle"
                  style={{ position: 'relative', bottom: 0, left: 0, width: '100%', borderRadius: 0 }}
                  onClick={() => setShowWatchLater(false)}
                >
                  Minimize
                </button>
              </div>
            )}
          </>
        )}

        {/* Idle Modal */}
        {showIdleModal && (
          <div className="idle-modal-overlay" onClick={() => setShowIdleModal(false)}>
            <div className="idle-modal" onClick={e => e.stopPropagation()}>
              <div className="idle-modal-icon">👀</div>
              <h3>Still watching?</h3>
              <p>Check out the latest trending videos or discover something new!</p>
              <div className="idle-modal-buttons">
                <button className="idle-modal-dismiss" onClick={() => setShowIdleModal(false)}>
                  I'm good
                </button>
                <button className="idle-modal-refresh" onClick={() => { handleRefresh(); setShowIdleModal(false); }}>
                  🔄 Refresh Feed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button className="scroll-top-button" onClick={scrollToTop} title="Back to top" aria-label="Back to top">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M12 4.5c.28 0 .55.11.75.32l6 6.25a1.06 1.06 0 0 1-1.53 1.46L13 8.06V19a1 1 0 1 1-2 0V8.06l-4.22 4.47a1.06 1.06 0 0 1-1.53-1.46l6-6.25c.2-.21.47-.32.75-.32Z" />
            </svg>
          </button>
        )}
      </div>
    </Layout>
  );
}

export default App;
